import React, { memo, useEffect, type FC } from "react";

import styled from "@emotion/styled";

import {
  paginationState,
  resultsState,
} from "Pages/RecipientsList/recoil/state/results";
import {
  DataTable,
  PARAM_ORDER_ASC,
  PARAM_ORDER_DESC,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  Badge,
} from "mailup-web-components";
import { useRecoilCallback, useRecoilValue } from "recoil";

import ItemSelect from "Pages/RecipientsList/components/results/ItemSelect";

import ItemSelectAll from "Pages/RecipientsList/components/results/ItemSelectAll";

import {
  getColumnName,
  getRecipientFieldValue,
} from "Pages/RecipientsList/recoil/services/results";

import { fetchResultsCallback } from "Pages/RecipientsList/recoil/callbacks/callbacks";
import { fetchRecipientsEngagementCallback } from "Pages/RecipientsList/recoil/callbacks/engagement";
import { fetchRecipientsGroupsCallback } from "Pages/RecipientsList/recoil/callbacks/groups";

import type {
  ColumnName,
  DynamicField,
  SearchResult,
} from "Pages/RecipientsList/model";
import {
  columnsState,
  dynamicFieldsState,
} from "Pages/RecipientsList/recoil/state/columns";

import { isSortableField } from "Pages/RecipientsList/recoil/services/columns";

import ItemContactField from "Pages/RecipientsList/components/results/ItemContactField";
import ItemEngagement from "Pages/RecipientsList/components/results/ItemEngagement";
import ItemGroups from "Pages/RecipientsList/components/results/ItemGroups";
import ItemStatus from "Pages/RecipientsList/components/results/ItemStatus";
import MainItem from "Pages/RecipientsList/components/results/MainItem";
import { useIntl } from "react-intl";

//  const MemoTeT = memo(TruncateAndTooltip)

interface SkelProps {
  variant: string;
}

const MemoSkel = memo<SkelProps>(SkeletonLoader);

const StyledSection = styled.section`
  & th.c-datatable__cell:not(:nth-of-type(1)) {
    padding-top: 3px;
  }
`;

export const toHeadColumn = (
  columnName: string,
  index: number,
  dynamicFields: DynamicField[],
  columnsLength: number,
): any => {
  return {
    width: index === 0 ? 250 : columnName === "Groups" ? 200 : undefined,
    stickyLeft: index === 0,
    truncate: index === 0,
    span:
      index === columnsLength - 1 || columnName === "Groups" ? undefined : 1,
    sortId: isSortableField(columnName) ? columnName : undefined,
    content: getColumnName(columnName, dynamicFields, true),
  };
};

export const toActions = (
  intl: any,
  items: SearchResult[],
  {
    onProfileClick,
    onEditClick,
    onUnsubscribeClick,
    onDeleteClick,
  }: {
    onProfileClick: (id: number) => void;
    onEditClick: (id: number) => void;
    onUnsubscribeClick: (id: number) => void;
    onDeleteClick: (id: number) => void;
  },
): any => {
  return items.map(({ Id, actions }) => ({
    disabled: false,
    items: [
      ...(actions.profile
        ? [
            {
              label: intl.formatMessage({
                id: "Load.RecipientsList.ActionViewProfile",
              }),
              onClick: onProfileClick,
              value: Id,
              "data-mu-test": "action-view-profile",
            },
          ]
        : []),
      ...(actions.edit
        ? [
            {
              label: intl.formatMessage({
                id: "Load.RecipientsList.ActionEdit",
              }),
              onClick: onEditClick,
              value: Id,
              "data-mu-test": "action-edit",
            },
          ]
        : []),

      //

      ...(actions.unsubscribe
        ? [
            {
              label: intl.formatMessage({
                id: "Load.RecipientsList.ActionUnsubscribe",
              }),
              onClick: onUnsubscribeClick,
              value: Id,
              "data-mu-test": "action-unsubscribe",
            },
          ]
        : []),

      //
      ...(actions.delete
        ? [
            {
              separator: true,
              label: intl.formatMessage({
                id: "Load.RecipientsList.ActionDelete",
              }),
              onClick: onDeleteClick,
              value: Id,
              "data-mu-test": "action-delete",
            },
          ]
        : []),
    ],
  }));
};

export const renderCell = (
  item: SearchResult,
  index: number,
  columnName: ColumnName,
  intl: any,
): any => {
  console.log("###item", item);
  if (index === 0) {
    if (columnName === "EmailSubscriptionStatus") {
      return (
        <ItemStatus
          id={item.Id}
          status={item.EmailSubscriptionStatus}
          isMainItem
        />
      );
    }

    if (columnName === "SMSSubscriptionStatus") {
      return (
        <ItemStatus
          id={item.Id}
          status={item.SMSSubscriptionStatus}
          isMainItem
        />
      );
    }

    if (columnName === "Engagement") {
      return <ItemEngagement id={item.Id} isMainItem />;
    }

    if (columnName === "Groups") {
      return <ItemGroups id={item.Id} isMainItem />;
    }

    if (columnName === "TrackingState") {
      return item.TrackingState === undefined ? (
        ""
      ) : (
        <Badge
          kind="outline"
          type={item.TrackingState ? "success" : "default"}
          text={
            item.TrackingState
              ? intl.formatMessage({
                  id: "Load.MailupCommon.Active",
                })
              : intl.formatMessage({
                  id: "Load.RecipientsList.NotTrack",
                })
          }
        />
      );
    }
    if (columnName === "TrackingLastChange") {
      return item.TrackingLastChange ?? "-";
    }

    return (
      <MainItem id={item.Id} label={getRecipientFieldValue(item, columnName)} />
    );
  }
  if (columnName === "TrackingState") {
    return item.TrackingState === undefined ? (
      ""
    ) : (
      <Badge
        kind="outline"
        type={item.TrackingState ? "success" : "default"}
        text={
          item.TrackingState
            ? intl.formatMessage({
                id: "Load.MailupCommon.Active",
              })
            : intl.formatMessage({
                id: "Load.RecipientsList.NotTrack",
              })
        }
      />
    );
  }
  if (columnName === "TrackingLastChange") {
    return item.TrackingLastChange ?? "-";
  }

  if (columnName === "EmailSubscriptionStatus") {
    return <ItemStatus id={item.Id} status={item.EmailSubscriptionStatus} />;
  }

  if (columnName === "SMSSubscriptionStatus") {
    return <ItemStatus id={item.Id} status={item.SMSSubscriptionStatus} />;
  }

  if (columnName === "Mobile") {
    return <ItemContactField label={item.Mobile} />;
  }

  if (columnName === "Email") {
    return <ItemContactField label={item.Email} />;
  }

  if (columnName === "Engagement") {
    return <ItemEngagement id={item.Id} />;
  }

  if (columnName === "Groups") {
    return <ItemGroups id={item.Id} />;
  }

  return getRecipientFieldValue(item, columnName);
};

const Results: FC = () => {
  const intl = useIntl();
  //  const navigate = useNavigate()

  const items = useRecoilValue(resultsState);
  const columns = useRecoilValue(columnsState);
  const dynamicFields = useRecoilValue(dynamicFieldsState);

  const { orderBy, orderDescending, loading, pageSize } =
    useRecoilValue(paginationState);

  const fetchResults = useRecoilCallback(fetchResultsCallback, []);

  const fetchEngagement = useRecoilCallback(
    fetchRecipientsEngagementCallback,
    [],
  );

  const fetchGroups = useRecoilCallback(fetchRecipientsGroupsCallback, []);

  useEffect(() => {
    void fetchEngagement();
  }, [fetchEngagement]);

  useEffect(() => {
    if (columns.includes("Groups")) {
      void fetchGroups();
    }
  }, [fetchGroups, columns]);

  const head = [
    {
      content: <ItemSelectAll />,
      stickyLeft: true,
      width: 70,
    },

    ...columns.map((columnName, index) =>
      toHeadColumn(columnName, index, dynamicFields, columns.length),
    ),
  ];

  /*

  const idList = storeGet(STORE_KEY_ID_LIST) as string

  const modalUnsubscribeOpen = useRecoilCallback(
    modalUnsubscribeOpenCallback,
    []
  )

  const modalDeleteOpen = useRecoilCallback(modalDeleteOpenCallback, [])



  const handleActionUnsubscribe = (id: number): void => {
    void modalUnsubscribeOpen(id)
  }

  const handleActionDelete = (id: number): void => {
    void modalDeleteOpen(id)
  }

  const actions = toActions(intl, items, {
    onProfileClick: handleActionProfile,
    onEditClick: handleActionEdit,
    onUnsubscribeClick: handleActionUnsubscribe,
    onDeleteClick: handleActionDelete
  })
  */

  const body = loading
    ? Array.from({ length: Math.max(5, pageSize) }).map((_) => [
        {
          content: <MemoSkel variant={SKELETON_LOADER_VARIANT_ONWHITE} />,
        },
        ...columns.map((_columnName) => ({
          content: <MemoSkel variant={SKELETON_LOADER_VARIANT_ONWHITE} />,
        })),
      ])
    : items.map((item, _idx) => {
        return [
          { content: <ItemSelect id={item.Id} /> },
          ...columns.map((columnName, colIndex) => ({
            content: renderCell(item, colIndex, columnName, intl),
          })),
        ];
      });

  const handleSort = (field: any): void => {
    if (field !== undefined) {
      void fetchResults({ orderBy: field, pageNumber: 1 });
    }
  };

  const sortOrder = orderDescending ? PARAM_ORDER_DESC : PARAM_ORDER_ASC;

  return (
    <StyledSection>
      <div
        className="p-recipients-list-portal"
        style={{ position: "relative" }}
      />
      {/* <pre>{JSON.stringify(items, null, 2)}</pre> */}
      {/* actions={actions} */}
      <DataTable
        sortable
        hScroll
        stickyHead
        head={head}
        body={body}
        sortBy={orderBy === "Id" ? undefined : orderBy}
        sortOrder={sortOrder}
        onClickSort={handleSort}
      />
    </StyledSection>
  );
};

export default Results;
